import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import NotificationBell from '@/components/NotificationBell';
import {
  Menu, X, ChevronDown, Search, ArrowUpRight, ArrowRight,
  LayoutDashboard, User, LogOut, HelpCircle, CreditCard, Shield, Award,
  Wallet, RefreshCw, Coins, MessageCircle, Mail, Building2, BadgeCheck,
  Command, Sparkles, ChefHat, Wrench, Briefcase, Factory, Layers,
} from 'lucide-react';
import api from '@/lib/api';
import {
  readCurrencyMode, writeCurrencyMode, formatAmount,
} from '@/lib/currency';
import usePlan, { getPlanTheme } from '@/hooks/usePlan';

const ease = [0.22, 1, 0.36, 1];

/* ════════════════════════════════════════════════════════════════════════
   ZERTIVO NAVBAR — Premium Cinematic Header (Iteration 153)
   ────────────────────────────────────────────────────────────────────────
   Design DNA: Linear × Stripe × Apple — pushed harder.

   States:
   ─ Top of dark heroes (/, /about, /directory profile pages):
     edge-to-edge transparent with soft black-top glow + ambient lift
   ─ Scrolled OR light routes:
     floating glass pill (centered, max 1280px) with backdrop-blur,
     subtle inner highlight, shadow-glow, accent-green hairline on hover

   Highlights:
   ─ Animated active-indicator (grüner Pulse-Dot) wandert via layoutId
   ─ Live status chip neben Logo (verifications-counter, animated)
   ─ ⌘K Search-Trigger mit sichtbarem keyboard-hint, ⌘+K binding
   ─ Mega-menu mit 2-col layout: industries + featured CTA
   ─ Mobile: slide-in von rechts, gestaffelte reveals, glass backdrop
   ════════════════════════════════════════════════════════════════════════ */

const NAV_LINKS = [
  {
    label: 'Models',
    items: [
      { label: 'Restaurants', desc: 'Vertrauen, das schmeckt.', href: '/for/restaurants', icon: ChefHat },
      { label: 'Handwerk', desc: 'Handarbeit, verifiziert.', href: '/for/handwerk', icon: Wrench },
      { label: 'Agenturen', desc: 'Pitch-ready Vertrauen.', href: '/for/agencies', icon: Briefcase },
      { label: 'Mittelstand', desc: 'Lieferanten in 60 Sekunden.', href: '/for/mittelstand', icon: Factory },
      { label: 'Alle Branchen', desc: 'Komplette Übersicht', href: '/industries', icon: Layers },
    ],
  },
  { label: 'Verzeichnis', href: '/directory', direct: true },
  { label: 'Methodik', href: '/methodology', direct: true },
  { label: 'Preise', href: '/pricing', direct: true },
  { label: 'Über uns', href: '/about', direct: true },
];

const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform || '');

function NavbarPlanPill() {
  const { planKey } = usePlan();
  const theme = getPlanTheme(planKey);
  return (
    <span className="inline-flex items-center gap-1 h-4 px-1.5 rounded-md bg-white/25 backdrop-blur-sm text-[9px] font-bold tracking-wider uppercase">
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: theme.accent }} />
      {theme.label}
    </span>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [userOpen, setUserOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchVal, setSearchVal] = useState('');
  const [wallet, setWallet] = useState(null);
  const [curMode, setCurMode] = useState(readCurrencyMode());
  const userRef = useRef(null);
  const closeMenuTimer = useRef(null);

  // Routes that have a dark/cinematic hero — navbar transparent at top
  const isHeroRoute = location.pathname === '/' || location.pathname === '/about';
  const isTransparent = isHeroRoute && !scrolled;

  // Active link tracking for animated pulse-dot
  const activePath = location.pathname;
  const isActive = (href) => href === '/' ? activePath === '/' : activePath.startsWith(href);

  // Wallet for company users
  useEffect(() => {
    if (!user || user.role !== 'company') { setWallet(null); return; }
    let cancelled = false;
    api.get('/wallet/me').then(({ data }) => { if (!cancelled) setWallet(data); }).catch(() => {});
    const onCur = () => setCurMode(readCurrencyMode());
    window.addEventListener('zertivo-currency-change', onCur);
    return () => { cancelled = true; window.removeEventListener('zertivo-currency-change', onCur); };
  }, [user, userOpen]);

  const toggleCurrency = () => {
    const next = curMode === 'EUR' ? 'VERIS' : 'EUR';
    setCurMode(next);
    writeCurrencyMode(next);
  };

  // Scroll detection — single rAF-throttled handler
  useEffect(() => {
    let raf;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        setScrolled(window.scrollY > 24);
        raf = null;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMobileOpen(false); setOpenMenu(null); setUserOpen(false); setSearchOpen(false);
  }, [location.pathname]);

  // ⌘K / Ctrl+K to open search; ESC to close any overlay
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false); setMobileOpen(false); setOpenMenu(null); setUserOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Click outside user dropdown
  useEffect(() => {
    const close = (e) => {
      if (userRef.current && !userRef.current.contains(e.target)) setUserOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchVal.trim();
    navigate(q ? `/directory?q=${encodeURIComponent(q)}` : '/directory');
    setSearchVal('');
    setSearchOpen(false);
  };

  const handleMenuEnter = useCallback((label) => {
    if (closeMenuTimer.current) clearTimeout(closeMenuTimer.current);
    setOpenMenu(label);
  }, []);
  const handleMenuLeave = useCallback(() => {
    closeMenuTimer.current = setTimeout(() => setOpenMenu(null), 120);
  }, []);

  // Color tokens that flip based on transparency
  const txt = isTransparent ? 'text-white' : 'text-[#09090B]';
  const subtxt = isTransparent ? 'text-white/65' : 'text-[#52525B]';
  const hover = isTransparent ? 'hover:text-white' : 'hover:text-[#09090B]';

  return (
    <>
      <motion.header
        className={`fixed top-0 inset-x-0 z-50 safe-top transition-[background,backdrop-filter,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          isTransparent
            ? 'bg-gradient-to-b from-black/35 via-black/8 to-transparent'
            : 'bg-white/75 backdrop-blur-2xl backdrop-saturate-150 border-b border-[#E4E4E7]/40 shadow-[0_1px_0_rgba(255,255,255,0.6)_inset,0_8px_32px_-12px_rgba(9,9,11,0.06)]'
        }`}
        data-testid="navbar"
      >
        <div className="max-w-[1440px] mx-auto px-5 sm:px-7 lg:px-10">
          <div className="flex items-center justify-between h-16 md:h-[68px]">
            {/* ─── LOGO + LIVE-STATUS-CHIP ─── */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <Link
                to="/"
                className={`group flex items-center ${txt} transition-colors`}
                data-testid="nav-logo"
                aria-label="Zertivo"
              >
                <span className="font-manrope text-[21px] md:text-[22px] font-light tracking-[-0.04em] inline-flex items-baseline relative">
                  <span className="font-extralight">Zertivo</span>
                  {/* Pulsing accent dot */}
                  <span className="ml-0.5 relative inline-block w-1.5 h-1.5">
                    <span className="absolute inset-0 rounded-full bg-[#00D387] opacity-80 group-hover:scale-[1.6] transition-transform duration-500" />
                    <span className="absolute inset-0 rounded-full bg-[#00D387] animate-ping opacity-40" />
                  </span>
                </span>
              </Link>
              {/* Live status chip — visible on md+ only */}
              <span
                className={`hidden md:inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-[9.5px] font-medium tracking-[0.16em] uppercase transition-all duration-500 ${
                  isTransparent
                    ? 'bg-white/10 border border-white/15 text-white/75 backdrop-blur-md'
                    : 'bg-[#0F8A5F]/8 border border-[#0F8A5F]/15 text-[#0F8A5F]'
                }`}
                data-testid="nav-live-status"
                title="Live · Plattform-Status"
              >
                <span className="relative flex w-1.5 h-1.5">
                  <span className="absolute inset-0 rounded-full bg-[#00D387] animate-ping opacity-60" />
                  <span className="relative w-1.5 h-1.5 rounded-full bg-[#00D387]" />
                </span>
                Live
              </span>
            </div>

            {/* ─── DESKTOP NAV (centered with active-indicator) ─── */}
            <nav
              className="hidden lg:flex items-center gap-1 flex-1 justify-center"
              aria-label="Primary"
              onMouseLeave={handleMenuLeave}
            >
              {NAV_LINKS.map((l) => (
                <NavItem
                  key={l.label}
                  link={l}
                  txt={txt}
                  hover={hover}
                  active={l.direct && isActive(l.href)}
                  open={openMenu === l.label}
                  onEnter={() => handleMenuEnter(l.label)}
                  onLeave={handleMenuLeave}
                  onClick={l.direct ? undefined : () => setOpenMenu(openMenu === l.label ? null : l.label)}
                  isTransparent={isTransparent}
                />
              ))}
            </nav>

            {/* ─── RIGHT CLUSTER ─── */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Search trigger with ⌘K hint */}
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                aria-label="Suche öffnen (Strg+K oder ⌘K)"
                data-testid="nav-search-toggle"
                className={`hidden sm:inline-flex items-center gap-2 h-9 pl-3 pr-2 rounded-full border transition-all duration-300 group ${
                  isTransparent
                    ? 'border-white/15 hover:border-white/30 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white backdrop-blur-md'
                    : 'border-[#E4E4E7] hover:border-[#09090B]/30 bg-white hover:bg-[#FAFAFA] text-[#52525B] hover:text-[#09090B]'
                }`}
              >
                <Search className="w-3.5 h-3.5" />
                <span className="hidden md:inline text-[12px] font-light tracking-tight">Suchen</span>
                <span
                  className={`hidden md:inline-flex items-center gap-0.5 ml-1 px-1.5 py-0.5 rounded-md text-[9.5px] font-mono font-bold tracking-wider transition-colors ${
                    isTransparent ? 'bg-white/10 text-white/60' : 'bg-[#F4F4F5] text-[#71717A] group-hover:bg-[#E4E4E7]'
                  }`}
                >
                  {isMac ? <Command className="w-2.5 h-2.5" /> : 'Ctrl'}
                  <span>K</span>
                </span>
              </button>

              {user ? (
                <>
                  <div className={isTransparent ? 'text-white' : ''}>
                    <NotificationBell />
                  </div>
                  <div ref={userRef} className="relative">
                    <button
                      onClick={() => setUserOpen(!userOpen)}
                      className={`group inline-flex items-center gap-2 ${txt} ${hover} transition-colors`}
                      data-testid="nav-user-btn"
                      aria-label="Mein Konto"
                    >
                      <span className={`relative w-8 h-8 rounded-full bg-gradient-to-br from-[#0F8A5F] to-[#00D387] flex items-center justify-center text-white text-[12px] font-bold transition-all duration-300 ${
                        isTransparent
                          ? 'ring-2 ring-white/30 group-hover:ring-white/60'
                          : 'ring-2 ring-white shadow-[0_2px_8px_rgba(15,138,95,0.3)] group-hover:shadow-[0_4px_16px_rgba(15,138,95,0.45)]'
                      }`}>
                        {(user.name || user.email || 'U')[0].toUpperCase()}
                      </span>
                      <ChevronDown className={`hidden sm:block w-3.5 h-3.5 transition-transform duration-200 ${userOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <UserDropdown
                      open={userOpen}
                      user={user}
                      wallet={wallet}
                      curMode={curMode}
                      toggleCurrency={toggleCurrency}
                      onClose={() => setUserOpen(false)}
                      onLogout={() => { logout(); navigate('/'); }}
                    />
                  </div>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    data-testid="nav-login-link"
                    className={`hidden sm:inline-flex items-center h-9 px-3 text-[12.5px] font-medium tracking-tight transition-colors ${
                      isTransparent ? 'text-white/80 hover:text-white' : 'text-[#52525B] hover:text-[#09090B]'
                    }`}
                  >
                    Anmelden
                  </Link>
                  <Link
                    to="/register"
                    data-testid="nav-register-cta"
                    className={`hidden sm:inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[12.5px] font-medium tracking-tight transition-all duration-300 active:scale-[0.97] ${
                      isTransparent
                        ? 'bg-white text-[#09090B] hover:bg-[#00D387] hover:text-[#04221A] hover:shadow-[0_8px_24px_-6px_rgba(0,211,135,0.5)]'
                        : 'bg-[#09090B] text-white hover:bg-[#0F8A5F] hover:shadow-[0_8px_24px_-6px_rgba(15,138,95,0.4)]'
                    }`}
                  >
                    Verifizieren
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </>
              )}

              <button
                onClick={() => setMobileOpen(true)}
                className={`lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
                  isTransparent ? 'text-white hover:bg-white/10' : 'text-[#09090B] hover:bg-[#F4F4F5]'
                }`}
                data-testid="mobile-menu-btn"
                aria-label="Menü öffnen"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* ─── MEGA-MENU (Models) ─── */}
        <AnimatePresence>
          {openMenu && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.28, ease }}
              onMouseEnter={() => handleMenuEnter(openMenu)}
              onMouseLeave={handleMenuLeave}
              className="hidden lg:block absolute top-full inset-x-0 bg-white border-b border-[#E4E4E7] shadow-[0_32px_80px_-24px_rgba(9,9,11,0.16)]"
              data-testid="nav-megamenu"
            >
              {/* Subtle accent line */}
              <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00D387]/40 to-transparent" />
              <div className="max-w-[1440px] mx-auto px-6 sm:px-8 lg:px-12 py-10 grid grid-cols-12 gap-8">
                {/* Industry items (8 cols) */}
                <div className="col-span-12 lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
                  <p className="col-span-full text-[10px] tracking-[0.28em] uppercase font-bold text-[#A1A1AA] -mb-2">
                    Branchen-Modelle
                  </p>
                  {(NAV_LINKS.find(l => l.label === openMenu)?.items || []).map(item => {
                    const Icon = item.icon || Building2;
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        className="group flex items-start gap-4 rounded-2xl p-3 -ml-3 hover:bg-[#FAFAFA] transition-colors"
                        data-testid={`mega-${item.label.toLowerCase()}`}
                      >
                        <span className="mt-0.5 w-10 h-10 rounded-xl bg-[#E6FBF3] text-[#0F8A5F] flex items-center justify-center flex-shrink-0 group-hover:bg-[#00D387] group-hover:text-white transition-colors">
                          <Icon className="w-4 h-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="font-manrope text-[16px] font-light text-[#09090B] tracking-tight group-hover:text-[#0F8A5F] transition-colors">
                            {item.label}
                          </p>
                          <p className="text-[12.5px] text-[#71717A] font-light mt-0.5 leading-relaxed">{item.desc}</p>
                        </div>
                        <ArrowUpRight className="w-3.5 h-3.5 text-[#A1A1AA] group-hover:text-[#0F8A5F] flex-shrink-0 mt-2 opacity-0 group-hover:opacity-100 transition-all -translate-x-1 group-hover:translate-x-0" />
                      </Link>
                    );
                  })}
                </div>

                {/* Featured CTA card (4 cols) */}
                <div className="hidden lg:block col-span-4">
                  <Link
                    to="/audit-check"
                    className="group relative block h-full rounded-2xl overflow-hidden bg-[#09090B] text-white p-6"
                  >
                    {/* Ambient green glow */}
                    <div aria-hidden className="absolute -top-12 -right-8 w-48 h-48 rounded-full bg-[#00D387] opacity-20 blur-[60px] group-hover:opacity-30 transition-opacity" />
                    <div className="relative flex items-center gap-1.5 mb-2">
                      <Sparkles className="w-3 h-3 text-[#00D387]" />
                      <span className="text-[9.5px] tracking-[0.24em] uppercase font-bold text-[#00D387]">Kostenlos · 60 Sek.</span>
                    </div>
                    <p className="relative font-manrope text-[24px] font-extralight tracking-[-0.025em] leading-[1.1]">
                      Schnell-Check.<br/>Sofortige <span className="italic font-light text-[#00D387]">Auswertung.</span>
                    </p>
                    <p className="relative mt-3 text-[12px] text-white/55 font-light leading-relaxed">
                      Ohne Login. Echte Trust-Indikatoren — direkt im Browser.
                    </p>
                    <span className="relative mt-5 inline-flex items-center gap-1.5 text-[12px] font-medium text-[#00D387] group-hover:text-white transition-colors">
                      Jetzt starten <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* ════════════════ Search Overlay (Command Palette feel) ════════════════ */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease }}
            className="fixed inset-0 z-[60] bg-[#09090B]/92 backdrop-blur-2xl flex items-start justify-center pt-24 sm:pt-32 px-5"
            onClick={() => setSearchOpen(false)}
            data-testid="nav-search-overlay"
          >
            <button
              type="button"
              aria-label="Suche schließen"
              onClick={() => setSearchOpen(false)}
              className="absolute top-6 right-6 sm:top-8 sm:right-8 w-11 h-11 inline-flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/5 transition-all"
            >
              <X className="w-5 h-5" />
            </button>
            <motion.form
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.99 }}
              transition={{ duration: 0.4, delay: 0.05, ease }}
              onClick={(e) => e.stopPropagation()}
              onSubmit={onSearchSubmit}
              className="w-full max-w-3xl"
            >
              <div className="flex items-center justify-between mb-5">
                <span className="font-inter text-[10.5px] tracking-[0.32em] uppercase font-medium text-white/50">Suche</span>
                <span className="hidden sm:inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase font-light text-white/35">
                  <kbd className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10 font-mono text-[10px]">ESC</kbd>
                  schließen
                </span>
              </div>
              <div className="flex items-center border-b border-white/30 focus-within:border-[#00D387]/70 transition-colors py-3">
                <Search className="w-6 h-6 text-white/55 flex-shrink-0" />
                <input
                  autoFocus
                  type="search"
                  value={searchVal}
                  onChange={(e) => setSearchVal(e.target.value)}
                  placeholder="Unternehmen, Branche oder Stadt"
                  className="flex-1 bg-transparent border-0 outline-none px-4 sm:px-6 py-2 text-[24px] sm:text-[34px] md:text-[42px] font-extralight text-white placeholder:text-white/30 tracking-[-0.025em]"
                  data-testid="nav-search-input"
                />
                <button
                  type="submit"
                  className="group inline-flex items-center gap-2 text-white hover:text-[#00D387] text-[14px] font-light tracking-wide pl-4 transition-colors"
                  data-testid="nav-search-submit"
                >
                  <span className="hidden sm:inline">Suchen</span>
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-0.5 transition-transform" />
                </button>
              </div>
              <div className="mt-7 flex flex-wrap items-center gap-2">
                <span className="text-[10.5px] tracking-[0.22em] uppercase font-medium text-white/35 mr-1">Beliebt:</span>
                {['Restaurants', 'Handwerk', 'Agenturen', 'IT', 'Berlin', 'München'].map(s => (
                  <button
                    type="button"
                    key={s}
                    onClick={() => { setSearchVal(s); navigate(`/directory?q=${encodeURIComponent(s)}`); setSearchOpen(false); }}
                    className="inline-flex h-8 px-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-[11.5px] font-light text-white/75 hover:text-white tracking-tight transition-all"
                    data-testid={`nav-search-suggest-${s.toLowerCase()}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ════════════════ Mobile Take-over ════════════════ */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.22, ease }}
            className="fixed inset-0 z-[60] bg-[#09090B] text-white flex flex-col safe-top safe-bottom overflow-hidden"
            data-testid="mobile-panel"
          >
            {/* Ambient green glows */}
            <div aria-hidden className="absolute -top-32 -right-24 w-96 h-96 rounded-full bg-[#00D387] opacity-[0.08] blur-[120px] pointer-events-none" />
            <div aria-hidden className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-[#0F8A5F] opacity-[0.06] blur-[140px] pointer-events-none" />

            <div className="relative flex items-center justify-between h-16 px-5">
              <Link to="/" onClick={() => setMobileOpen(false)} className="font-manrope text-[22px] font-extralight tracking-[-0.04em] inline-flex items-baseline">
                Zertivo<span className="ml-0.5 inline-block w-1.5 h-1.5 rounded-full bg-[#00D387]" />
              </Link>
              <button
                onClick={() => setMobileOpen(false)}
                className="w-10 h-10 inline-flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/5"
                aria-label="Menü schließen"
                data-testid="mobile-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="relative flex-1 overflow-y-auto px-5 pt-6 pb-12">
              {/* Search */}
              <motion.form
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.05, ease }}
                onSubmit={onSearchSubmit}
                className="border-b border-white/25 pb-3 mb-8 flex items-center"
              >
                <Search className="w-4 h-4 text-white/55 flex-shrink-0" />
                <input
                  type="search"
                  value={searchVal}
                  onChange={(e) => setSearchVal(e.target.value)}
                  placeholder="Suche…"
                  className="flex-1 bg-transparent border-0 outline-none px-3 py-2 text-[16px] font-light text-white placeholder:text-white/35"
                  data-testid="mobile-search-input"
                />
              </motion.form>

              {/* Nav links — staggered reveal */}
              <nav className="space-y-1">
                <p className="text-[9.5px] tracking-[0.28em] uppercase font-bold text-white/40 mb-3">Navigation</p>
                {NAV_LINKS.flatMap((l) => l.direct
                  ? [{ label: l.label, href: l.href }]
                  : l.items
                ).map((item, i) => (
                  <motion.div
                    key={item.href}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.08 + i * 0.04, ease }}
                  >
                    <Link
                      to={item.href}
                      onClick={() => setMobileOpen(false)}
                      className="block py-3.5 border-b border-white/8 group"
                      data-testid={`mobile-link-${item.label.toLowerCase().replace(/\s+/g,'-')}`}
                    >
                      <span className="font-manrope text-[24px] sm:text-[28px] font-extralight tracking-[-0.025em] text-white group-hover:text-[#00D387] transition-colors inline-flex items-center gap-3">
                        <span className="text-[10px] tracking-[0.2em] text-white/35 font-mono">{String(i + 1).padStart(2, '0')}</span>
                        {item.label}
                      </span>
                      {item.desc && (
                        <p className="text-[12.5px] text-white/50 font-light mt-1 ml-8">{item.desc}</p>
                      )}
                    </Link>
                  </motion.div>
                ))}
              </nav>

              {/* Auth Section */}
              {!user ? (
                <motion.div
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.4, ease }}
                  className="mt-10 space-y-3"
                >
                  <Link
                    to="/register"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-between px-5 py-4 rounded-2xl bg-[#00D387] text-[#04221A] font-medium text-[14px] active:scale-[0.98] transition-transform"
                  >
                    <span>Jetzt verifizieren lassen</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/login"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-between px-5 py-4 rounded-2xl bg-white/5 border border-white/10 text-white text-[14px] font-light active:scale-[0.98] transition-transform"
                  >
                    <span>Anmelden</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.4, ease }}
                  className="mt-10 space-y-3"
                >
                  <Link
                    to={user.role === 'admin' || user.role === 'super_admin' ? '/admin' : '/portal'}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-between px-5 py-4 rounded-2xl bg-white/5 border border-white/10 text-white text-[14px] font-light active:scale-[0.98] transition-transform"
                  >
                    <span>Dashboard öffnen</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <button
                    type="button"
                    onClick={() => { logout(); setMobileOpen(false); navigate('/'); }}
                    className="w-full flex items-center justify-between px-5 py-4 rounded-2xl bg-transparent border border-white/10 text-white/70 hover:text-white text-[14px] font-light text-left"
                  >
                    <span>Abmelden</span>
                    <LogOut className="w-4 h-4" />
                  </button>
                </motion.div>
              )}

              {/* Footer mini meta */}
              <div className="mt-12 flex items-center gap-2 text-[10.5px] tracking-[0.18em] uppercase font-medium text-white/35">
                <span className="relative flex w-1.5 h-1.5">
                  <span className="absolute inset-0 rounded-full bg-[#00D387] animate-ping opacity-50" />
                  <span className="relative w-1.5 h-1.5 rounded-full bg-[#00D387]" />
                </span>
                Live · Alle Systeme normal
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/* NAV ITEM — direct link OR mega-menu trigger, with animated active dot     */
/* ────────────────────────────────────────────────────────────────────────── */
function NavItem({ link, txt, hover, active, open, onEnter, onLeave, onClick, isTransparent }) {
  const baseCls = `relative inline-flex items-center gap-1 h-9 px-3 ${txt} ${hover} text-[13px] font-light tracking-tight transition-colors duration-200`;

  const dot = active && (
    <motion.span
      layoutId="nav-active-dot"
      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
      className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#00D387]"
      style={{ boxShadow: isTransparent ? '0 0 8px rgba(0,211,135,0.7)' : '0 0 6px rgba(0,211,135,0.5)' }}
    />
  );

  if (link.direct) {
    return (
      <Link
        to={link.href}
        onMouseEnter={onLeave}
        className={baseCls}
        data-testid={`nav-link-${link.label.toLowerCase()}`}
      >
        {link.label}
        {dot}
      </Link>
    );
  }
  return (
    <button
      onMouseEnter={onEnter}
      onClick={onClick}
      className={baseCls}
      aria-expanded={open}
      data-testid={`nav-menu-${link.label.toLowerCase()}`}
    >
      {link.label}
      <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/* USER DROPDOWN — minimal Apple-style sheet (unchanged behavior)             */
/* ────────────────────────────────────────────────────────────────────────── */
function UserDropdown({ open, user, wallet, curMode, toggleCurrency, onClose, onLogout }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 8, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 6, scale: 0.99 }}
          transition={{ duration: 0.2, ease }}
          className="fixed left-2 right-2 top-[72px] sm:absolute sm:left-auto sm:right-0 sm:top-12 sm:w-[320px] max-h-[80vh] overflow-y-auto rounded-2xl bg-white border border-[#E4E4E7] shadow-[0_24px_60px_-20px_rgba(0,0,0,0.16)] z-50"
          data-testid="nav-user-dropdown"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-5 border-b border-[#F4F4F5]">
            <p className="font-manrope text-[15px] font-light text-[#09090B] tracking-tight">{user.name || user.email}</p>
            <p className="text-[12px] text-[#71717A] truncate font-light">{user.email}</p>
            <div className="mt-2 flex items-center gap-1.5">
              <span className="inline-flex items-center h-4 px-1.5 rounded-md bg-[#0F8A5F] text-white text-[9px] font-bold tracking-wider uppercase">
                {user.role === 'super_admin' ? 'Admin' : user.role === 'admin' ? 'Admin' : user.role === 'auditor' ? 'Auditor' : 'Company'}
              </span>
              <NavbarPlanPill />
            </div>
          </div>

          {user.role === 'company' && wallet && (
            <div className="px-5 py-4 border-b border-[#F4F4F5]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] tracking-[0.18em] uppercase font-medium text-[#71717A]">Guthaben</span>
                <button
                  onClick={toggleCurrency}
                  data-testid="nav-currency-toggle"
                  className="inline-flex items-center gap-1 text-[10px] font-medium tracking-wider uppercase text-[#0F8A5F] hover:text-[#065F43]"
                >
                  {curMode === 'EUR' ? <><RefreshCw className="w-2.5 h-2.5" /> EUR</> : <><Coins className="w-2.5 h-2.5" /> VERIS</>}
                </button>
              </div>
              <p className="font-manrope text-[24px] font-light tabular-nums text-[#09090B]">
                {formatAmount(wallet.balance, curMode)}
              </p>
              <Link
                to="/portal/wallet"
                onClick={onClose}
                className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-light text-[#0F8A5F] hover:text-[#065F43] border-b border-[#0F8A5F]/40 hover:border-[#0F8A5F] pb-0.5"
              >
                Aufladen <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
          )}

          <div className="p-2">
            <DropLink to={user.role === 'admin' || user.role === 'super_admin' || user.role === 'support' ? '/admin' : user.role === 'auditor' ? '/auditor' : '/portal'} icon={LayoutDashboard} onClose={onClose}>Dashboard</DropLink>
            {user.role === 'company' && (
              <>
                <DropLink to="/portal/company" icon={Building2} onClose={onClose}>Profil</DropLink>
                <DropLink to="/portal/billing" icon={CreditCard} onClose={onClose}>Abrechnung</DropLink>
                <DropLink to="/portal/badges" icon={Award} onClose={onClose}>Badges</DropLink>
                <DropLink to="/portal/wallet" icon={Wallet} onClose={onClose}>Guthaben</DropLink>
              </>
            )}
            <DropLink to="/admin/profile" icon={User} onClose={onClose}>Mein Profil</DropLink>
            <DropLink to="/admin/security" icon={Shield} onClose={onClose}>Sicherheit & 2FA</DropLink>
            <DropLink to="/faq" icon={HelpCircle} onClose={onClose}>Hilfe & FAQ</DropLink>
            <DropLink to="/contact" icon={Mail} onClose={onClose}>Kontakt</DropLink>
            <button
              type="button"
              onClick={() => {
                onClose();
                window.dispatchEvent(new CustomEvent('zv:open-support-chat'));
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#FAFAFA] text-left"
              data-testid="nav-open-chat-btn"
            >
              <MessageCircle className="w-4 h-4 text-[#0F8A5F]" />
              <span className="flex-1 text-[13px] font-light text-[#09090B]">Live-Chat</span>
              <span className="text-[9px] tracking-[0.18em] uppercase font-medium text-[#0F8A5F]">Online</span>
            </button>
          </div>

          <div className="p-2 border-t border-[#F4F4F5]">
            <button
              type="button"
              onClick={onLogout}
              data-testid="nav-logout-btn"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#FEF2F2] text-left"
            >
              <LogOut className="w-4 h-4 text-[#B91C1C]" />
              <span className="flex-1 text-[13px] font-light text-[#B91C1C]">Abmelden</span>
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function DropLink({ to, icon: Icon, onClose, children }) {
  return (
    <Link
      to={to}
      onClick={onClose}
      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#FAFAFA] text-[13px] font-light text-[#09090B]"
    >
      <Icon className="w-4 h-4 text-[#52525B]" />
      <span className="flex-1">{children}</span>
    </Link>
  );
}
